import { Component, HostListener, Inject, OnInit, PLATFORM_ID, ViewChild, WritableSignal, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SignalsStoreService } from '../signals-store.service';
import { AuthService } from '../../authentication/auth.service';
import { LocalStorageService } from '../local-storage.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { ModalContentService } from '../modal-content/modal-content.service';
import { ModalContentTypes } from '../constants/modal-content-types';
import { FormsModule } from '@angular/forms';
import { ModalContentData } from '../modal-content/modal-content';
import { isPlatformBrowser, CommonModule, Location } from '@angular/common';
import { environment } from '../../../environments/environment';
import { combineLatest, filter, tap } from 'rxjs';
import { SearchBarComponent } from './search-bar/search-bar.component';
import { HeaderService } from './header.service';
import { MenuService } from '../menu.service';
import { ProductsService } from '../../product/products.service';
import { FIREBASE_COLLECTIONS, LOCALSTORAGE_KEYS } from '../constants/databases';
import { Session } from '../types/session.type';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { OrderService } from '../order.service';
import { SubscriptionsService } from '../../settings/account/subscriptions/subscriptions.service';
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatOption } from "@angular/material/autocomplete";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { ResolutionService } from '../resolution.service';
import { NAVIGATION_ITEMS, RIGHT_ELEMENTS, SETTINGS_PAGES } from './header.lists';
import { StockService } from '../../stock/stock.service';
import { AccountService } from '../account.service';
import { eCommercePermissions } from '../types/account.types';
import { Meta, Title } from '@angular/platform-browser';
import { formatDateToReadableString } from '../utils/formatting';
import { KlaviyoService } from '../klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from '../klaviyo/events';
import { formatStringForURL, isAddressUpdateRequired, getCurrentPathFromUrl } from '../common/utils';
import { DateTime } from 'luxon';
import { FirebaseCrudService } from '../firebase-crud.service';
import { DataLayerService } from '../data-layer/data-layer.service';
import { PastDueService } from '../past-due.service';
import { SidebarService } from '../sidebar/sidebar.service';
import { SidebarMenu } from '../sidebar/sidebar.types';
import { toObservable } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    FormsModule,
    SearchBarComponent,
    CommonModule,
    MatFormField,
    MatLabel,
    MatOption,
    RouterLinkActive,
    MatSelectModule,
    NgbModule,
  ],
  providers: [SubscriptionsService],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit {
  signalsStoreService = inject(SignalsStoreService);
  modalContentService = inject(ModalContentService);
  authService = inject(AuthService);
  HAS_MEMBERSHIP_FLOW: boolean = !!environment.config.flows.membership;

  private localStorageService = inject(LocalStorageService);
  private deliveriesService = inject(DeliveriesService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private headerService = inject(HeaderService);
  private menuService = inject(MenuService);
  private productService = inject(ProductsService);
  private activeModal = inject(NgbModal);
  private orderService = inject(OrderService);
  private location = inject(Location);
  private subscriptionsService = inject(SubscriptionsService);
  private resolutionService = inject(ResolutionService);
  private stockService = inject(StockService);
  private productsService = inject(ProductsService);
  private accountService = inject(AccountService);
  private meta = inject(Meta);
  private title = inject(Title);
  private klaviyoService = inject(KlaviyoService);
  private firebaseService = inject(FirebaseCrudService);
  #sidebarService = inject(SidebarService);
  #dataLayerService = inject(DataLayerService);
  #pastDueService = inject(PastDueService);

  orderProductsQuantity = computed(() => this.getOrderProductsQuantity());
  isSessionLoaded = computed(() => this.signalsStoreService.isSessionLoaded());
  shouldShowCategoriesBar = computed(() => this.signalsStoreService.shouldShowCategoriesBar());

  routeSignal = signal(this.router.url);

  isShowingSearchBar = computed(() => !!(this.headerService.displaySearchBar() || this.signalsStoreService.filterByProducer()));
  isSidebarOpen = this.signalsStoreService.isSidebarOpen;

  isShowingSearchMobile = computed(() => (!!(this.headerService.displaySearchBar() || this.signalsStoreService.filterByProducer()) && this.isMobile()));

  // Search-results banner state (exposed so the mobile sub-nav can render it in
  // place of the Category/SubCategory dropdowns while a search is active).
  isInSearchMode = this.signalsStoreService.isInSearchMode;
  searchModeQuery = this.signalsStoreService.searchModeQuery;
  searchResultCount = this.signalsStoreService.searchResultCount;

  userName: WritableSignal<string> = signal('');

  menuItems = computed(() => this.menuService.menu());

  submenu = computed(() => this.#getSubMenu());

  isMobile = computed(() => this.resolutionService.isMobile());

  landingPages = signal(NAVIGATION_ITEMS);
  settingsPages = computed(() => this.setUpSettingsPermissions());
  rightElements = computed(() => this.setUpRightElementPermissions());

  isShowingMobileFooter = this.signalsStoreService.isShowingMobileFooter;

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  isContentLoading = computed(() => this.stockService.isLoading() || this.productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.productsService.isLoaded());

  session = computed(() => this.signalsStoreService.sessionSignal());

  subcategorySelected!: number;

  @ViewChild(MatSelect) matSelect!: MatSelect;

  $isLoadedDeliveryInfo = toObservable(this.signalsStoreService.isLoadedDeliveryInfo);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.#categoryInViewPort();
  }

  #getSubMenu(): SidebarMenu[] {

    const category = this.#sidebarService.sidebarMenu();

    if (category?.length)
      return category[0].sublist ?? [];
    else
      return [];
  }

  #categoryInViewPort() {
    toObservable(
      this.signalsStoreService
        .categoryInViewPort
    )
      .subscribe(obs => {
        if (!obs) return;
        this.subcategorySelected = obs.subCategoryId;
      });
  }

  getOrderProductsQuantity() {
    const odooOrder = this.orderService.order();
    const allProducts = [
      ...(odooOrder?.products?.common ?? []),
      ...(odooOrder?.products?.subscription?.length ? odooOrder?.products?.subscription.map((e: any) => {
        return {
          ...e,
          _isSub: true
        }
      }) : []),
    ];

    if (odooOrder?.isSkipped) return 0;

    return new Set(allProducts.map((product: any) => {
      const variantId = product.variant.id;
      const packageId = product.package?.id || null;
      const id = `${variantId}${packageId ? `_${packageId}` : ''}`;
      if (product._isSub) return `${id}_sub`;
      return id
    })).size;
  }

  logOut() {
    this.authService.logout();
  }

  ngOnInit(): void {
    if (this.signalsStoreService.filterByProducer()) {
      this.headerService.displaySearchBar.set(true);
    }
    this.subscribeToRouteChanges();
    this.#getAndSyncSubscriptionsTrigger();
    this.setUpMenu();
    this.signalsStoreService.getAndSyncExpiredOrder();

    this.setUpUserSession();
    this.getSubscriptions();
  }

  private setUpMenu() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.menuService.fetchMenuCategories().subscribe({
      next: () => this.setUpSelectedCategory(this.location.path()),
    });
  }

  private setUpUserSession() {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    const permissions: eCommercePermissions | null = this.localStorageService.get(LOCALSTORAGE_KEYS.PERMISSIONS);
    if (session?.accountInfo?.id) {
      this.#getUserPreferences(session?.accountInfo?.id);
      this.accountService.getMembershipPermissions().subscribe();
      this.headerService.getFrequencies();
      this.userName.set(session.accountInfo.firstName.toUpperCase());
      if (
        !(!!(permissions?.settings.paymentMethod.allowed) && !(session?.settings?.hasPaymentMethod) && !session?.settings?.isExplorer) &&
        !(!!(session?.settings?.requireAddress) && !(session?.address?.street))
      ) {
        this.getOrder();
      }
      this.deliveriesService
        .getDeliveryZoneInfo()
        .pipe(
          tap(() => this.validateNewUser()),
          tap(() => this.signalsStoreService.isLoadedDeliveryInfo.set(true))
        )
        .subscribe();
    }
  }

  toggleSearchBar() {
    // this.isShowingSearchBar.update((current: boolean) => !current);
    this.headerService.updateSearchBarDisplay();
  }
  toggleSearchMobile() {
    this.headerService.updateSearchBarDisplay();
    if (this.signalsStoreService.isFiltering())
      this.headerService.clearFilters(true);
  }

  /**
   * Exit search-results mode from the mobile sub-nav banner. Delegates to
   * `clearFilters(true)` which reloads the active category, and additionally
   * strips ?q= from the URL when present (clearFilters only navigates when the
   * URL doesn't already match the category path).
   */
  clearMobileSearch() {
    this.signalsStoreService.searchResultCount.set(0);
    this.headerService.clearFilters(true);

    if (this.activatedRoute.snapshot.queryParamMap.has('q')) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { q: null },
        queryParamsHandling: 'merge',
      });
    }

    this.signalsStoreService.restorePreSearchScroll(getCurrentPathFromUrl(this.router.url));
  }

  toggleCategorySideMenu() {
    this.signalsStoreService.isSidebarOpen.update(
      (currentValue) => !currentValue
    );
  }

  private subscribeToRouteChanges() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.routeSignal.set(this.router.url);
        this.setUpSelectedCategory(event.url);
      });
  }

  private getOrder() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.signalsStoreService.hasSession()) return;
    const currentUrl = this.router.url;
    if ((!currentUrl.includes('/order') || currentUrl.includes('/order-and-billing')) && !currentUrl.includes('/cart'))
      this.orderService.getOrder(this.signalsStoreService.marketStatus)
  }

  openModalHowItWorks() {
    const sessionData = (): any => {
      const hasSession = this.signalsStoreService.hasSession();
      const userData: Session | null = hasSession
        ? this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION)
        : null;
      return {
        hasSession,
        fullScreenOnMobile: true,
        howItWorks: {
          ...userData?.deliveryInfo,
        },
      } as ModalContentData;
    };
    const data: ModalContentData = sessionData();
    this.modalContentService.openModal(ModalContentTypes.HOW_IT_WORKS, data);
  }

  triggerCategoryInfoFetch(
    category: { id: number; name: string },
    navigate: boolean = true
  ) {
    if (this.isContentLoading()) return;
    const formattedCategoryName = formatStringForURL(category.name);
    const parts = this.location.path().split('/');
    const currentCategoryName = parts[parts.length - 1].toLowerCase();
    if (formattedCategoryName === currentCategoryName && navigate && parts.length === 3) return;
    navigate ? this.router.navigate([`/shop/${formattedCategoryName}`]) : null;
    this.signalsStoreService.selectedCategory.set(category);
    this.#sidebarService.clearFilter();
    this.setUpSEOTags(category.name, formattedCategoryName);
    this.localStorageService.set(LOCALSTORAGE_KEYS.SELECTED_CATEGORY, category);
  }

  private setUpSEOTags(categoryName: string, formattedCategoryName: string) {
    const { baseTitle, baseURL, type } = environment.config.seo;
    const url = `shop/${formattedCategoryName}`;
    const title = `${categoryName} | ${baseTitle}`;

    // Page title
    this.title.setTitle(title);

    // Open Graph for social networks:
    this.meta.addTag({ property: 'og:title', content: title });
    this.meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.meta.addTag({ property: 'og:type', content: type });
  }

  /**
   * Past-due warning (DEV-10295). Shown once per browser session so a customer
   * who dismisses it is not nagged on every navigation, and never for suspended
   * accounts, which have their own screen.
   *
   * Returns true only when it actually opens the modal in this load, so the
   * caller knows whether the slot was taken.
   */
  private validatePastDueModal(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const session = this.signalsStoreService.sessionSignal();
    const billing = session?.billing;

    if (!billing?.isAtRisk) return false;
    if (session?.settings?.isSuspended) return false;

    const PAST_DUE_SHOWN_KEY = 'pastDueModalShown';
    if (sessionStorage.getItem(PAST_DUE_SHOWN_KEY)) return false;

    this.modalContentService.openModal(
      ModalContentTypes.ACCOUNT_PAST_DUE,
      {
        closeable: true,
        pastDue: {
          pastDueBalance: billing.pastDueBalance ?? 0,
          daysUntilSuspension: billing.daysUntilSuspension ?? null,
          invoiceHistoryPath: billing.invoiceHistoryPath ?? '/settings/billing/invoice-history',
        },
      },
    );

    sessionStorage.setItem(PAST_DUE_SHOWN_KEY, '1');

    return true;
  }

  private validateNewUser() {
    // A past-due warning outranks the welcome message: the customer needs to
    // know their deliveries are at risk before anything else competes for the
    // screen.
    //
    // The flag it reads is a login-time snapshot, so it is checked against the
    // live past-due invoices before anything opens (DEV-10988). Customers who
    // are not flagged get a synchronous answer here, which is why the rest of
    // the load-time modals are not waiting on a round trip.
    this.#pastDueService.revalidate().subscribe(isAtRisk => {
      if (isAtRisk && this.validatePastDueModal()) return;
      this.validateLoadModals();
    });
  }

  /** The load-time modals that queue behind the past-due warning. */
  private validateLoadModals() {
    isAddressUpdateRequired(this.localStorageService, this.modalContentService, this.activeModal, this.router);
    const newUser = this.localStorageService.get(LOCALSTORAGE_KEYS.NEW_ACCOUNT);
    if (newUser) {
      this.openModalHowItWorks();
      this.localStorageService.remove(LOCALSTORAGE_KEYS.NEW_ACCOUNT);
    }
  }

  private setUpSelectedCategory(url: string) {
    if (this.signalsStoreService.filterByProducer()) return;
    if (url.indexOf('/shop') > -1) {
      const parts = url.split('/');
      const shopIndex = parts.findIndex(e => e === 'shop');
      const categoryIndex = shopIndex === parts.length - 1
        ? shopIndex
        : shopIndex + 1;

      let categoryName = parts[categoryIndex].toLowerCase();
      // Validation for query params:
      if (categoryName.indexOf('?') > -1) categoryName = categoryName.split('?')[0];
      if (!this.menuItems().length) return;
      let category = undefined;
      if (categoryName === 'shop') {
        if (
          this.signalsStoreService.isFiltering() &&
          this.signalsStoreService.selectedCategory().id > -1
        ) {
          category = this.signalsStoreService.selectedCategory();
        } else {
          category = this.menuItems()[0];
        }
      } else
        category = this.menuItems().find(
          (e: any) => formatStringForURL(e.name) === categoryName
        );
      if (!category) return;
      this.triggerCategoryInfoFetch(category, categoryName === 'shop');
      // Get products info only if the user is not filtering.
      if (!this.signalsStoreService.isFiltering())
        this.productService.getProductsResume(category.id, true);
    } else {
      this.signalsStoreService.selectedCategory.set({ id: -1, name: '' });
      this.#sidebarService.sidebarMenu.set([]);
    }
  }

  private getSubscriptions() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.signalsStoreService.hasSession()) return;
    this.subscriptionsService.get();
  }

  private setUpSettingsPermissions() {
    const permissions = this.signalsStoreService.permissions();
    if (!permissions) return;

    // Set Up Settings Pages:
    const items = JSON.parse(JSON.stringify(SETTINGS_PAGES))
    const settingPages = items.map((p: { children: any[]; }) => {
      const children = p.children.filter((c: { permissionName: string; }) => {
        const keys = c.permissionName.split('.');
        let permission: any = permissions;
        for (const key of keys) {
          permission = permission[key]
        }

        if (permission.eCommRoute === '/settings/billing/membership' && this.signalsStoreService.isExplorerUser()) return false;

        return permission.allowed
      });
      p.children = children || [];
      return p
    }).filter((p: { children: string | any[]; }) => p.children?.length);

    return settingPages;
  }

  private setUpRightElementPermissions() {
    // Get the local storage permissions variable:
    const permissions: any = this.signalsStoreService.permissions();
    if (!permissions) return;

    // Set Up Right Elements:
    const items = JSON.parse(JSON.stringify(RIGHT_ELEMENTS));
    const rightElements = items.filter((p: { showWithoutPermission: any; permissionName: string; }) => {
      if (p.showWithoutPermission) return true;
      const keys = p.permissionName.split('.');
      let permission = permissions;
      for (const key of keys) {
        permission = permission[key]
      }
      return permission.allowed
    });

    return rightElements || []
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const targetElement = event.target as HTMLElement;
    if (this.matSelect?.panelOpen && !this.matSelect?._elementRef.nativeElement.contains(targetElement)) {
      this.matSelect.close();
    }
  }

  async #getUserPreferences(userId: number) {
    this.firebaseService.getById(FIREBASE_COLLECTIONS.PREFERENCES, userId.toString(), (result) => {
      if (!result) {
        return this.signalsStoreService.userPreferences.set({
          dontShowAgainPrevDislike: {
            date: 0,
            value: false
          },
          holdUpSubscriptions: {
            date: 0,
            value: false
          }
        });
      }
      this.signalsStoreService.userPreferences.set(result);
      this.#validateExpiredUserPreferences(userId, result);
    })
  }

  #validateExpiredUserPreferences(userId: number, preferences: { [key: string]: { value: boolean, date: number } }) {

    let update = false;

    for (const key in preferences) {

      const { value, date } = preferences[key];

      if (!value)
        continue;

      const diffMonths = DateTime.utc().diff(DateTime.fromMillis(date), 'months').months;

      if (diffMonths >= 3) {
        preferences[key].value = false;
        preferences[key].date = 0;
        update = true;
      }
    }

    if (!update)
      return;

    this.firebaseService.update(FIREBASE_COLLECTIONS.PREFERENCES, userId, preferences);
  }

  goToSection(item: SidebarMenu) {

    const element = document.getElementById(item.key as string);

    if (element)
      element?.scrollIntoView(true);
    else if (item.sublist && item.sublist.length)
      document.getElementById(item.sublist[0].key as string)?.scrollIntoView(true);
  }

  #getAndSyncSubscriptionsTrigger() {
    this.signalsStoreService.getAndSyncSubscriptionTrigger((res) => {
      if (res?.shouldRefresh && !this.signalsStoreService.isSubscriptionUpdateOrigin()) {
        this.orderService.getOrder(this.signalsStoreService.marketStatus);
        this.subscriptionsService.get();
      }
      this.signalsStoreService.isSubscriptionUpdateOrigin.set(false);
    });
  }
}
